/**
 * PropertyMap Component
 * 
 * Interactive map showing property locations using Leaflet + OpenStreetMap
 * Free, no API key required, fully customizable
 */

import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Link } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ProIcon } from './ProIcon';

// Fix default marker icon issue in Leaflet with webpack/vite
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

interface Property {
  id: string;
  title: string;
  location: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  guests: number;
  images: string[];
  badges: string[];
  lat: number;
  lng: number;
}

interface PropertyMapProps {
  properties: Property[];
  selectedPropertyId?: string | null;
}

// Component to handle map resize
const MapResizer: React.FC = () => {
  const map = useMap();
  
  useEffect(() => {
    const handleResize = () => {
      setTimeout(() => {
        map.invalidateSize();
      }, 100);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Initial invalidate after mount
    setTimeout(() => {
      map.invalidateSize();
    }, 200);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [map]);
  
  return null;
};

export const PropertyMap: React.FC<PropertyMapProps> = ({ 
  properties, 
  selectedPropertyId 
}) => {
  // Center on selected property, or first property, or fallback to Marbella center
  const selectedProperty = selectedPropertyId 
    ? properties.find(p => p.id === selectedPropertyId) 
    : properties.length === 1 ? properties[0] : null;
  const mapCenter: L.LatLngExpression = selectedProperty 
    ? [selectedProperty.lat, selectedProperty.lng] 
    : [36.5102, -4.8850];

  // Custom marker icon with brand color - using logo SVG
  const customIcon = L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      background: #f8951f;
      width: 36px;
      height: 36px;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 3px 10px rgba(0,0,0,0.3);
      border: 3px solid white;
    ">
      <img src="/src/assets/logos/sunmarbella-icon-white.svg" style="width: 18px; height: 18px; transform: rotate(45deg); filter: brightness(0) invert(1);" />
    </div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    popupAnchor: [0, -36]
  });

  const selectedIcon = L.divIcon({
    className: 'custom-marker-selected',
    html: `<div style="
      background: #126588;
      width: 44px;
      height: 44px;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 15px rgba(18,101,136,0.4);
      border: 4px solid white;
    ">
      <img src="/src/assets/logos/sunmarbella-icon-white.svg" style="width: 22px; height: 22px; transform: rotate(45deg); filter: brightness(0) invert(1);" />
    </div>`,
    iconSize: [44, 44],
    iconAnchor: [22, 44],
    popupAnchor: [0, -44]
  });

  return (
    <MapContainer
      center={mapCenter as any}
      zoom={selectedProperty ? 15 : 13}
      scrollWheelZoom={true}
      style={{ height: '100%', width: '100%' }}
      className="property-map"
    >
      <MapResizer />
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      
      {properties.map((property) => (
        <Marker
          key={property.id}
          position={[property.lat, property.lng] as any}
          icon={selectedPropertyId === property.id ? selectedIcon : customIcon}
        >
          <Popup 
            minWidth={220}
            maxWidth={220}
            closeButton={false}
            autoPan={true}
            autoPanPadding={[24, 24]}
          >
            <Link 
              to={`/property/${property.id}`}
              className="block group bg-white rounded-xl overflow-hidden"
            >
              <div className="w-full h-32 overflow-hidden">
                <img 
                  src={property.images[0]} 
                  alt={property.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="p-2.5">
                <h3 className="font-black text-brand-ocean text-sm leading-tight line-clamp-1 uppercase">
                  {property.title}
                </h3>
                <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-2">
                  <ul className="flex items-center justify-between text-sm text-slate-600 flex-[4]" aria-label="Property details">
                    <li className="flex flex-col items-center gap-1">
                      <span className="w-8 h-8 rounded-full bg-brand-sand/50 flex items-center justify-center">
                        <ProIcon icon="bed" family="light" className="w-4 h-4 text-brand-ocean" />
                      </span>
                      <span className="font-semibold">{property.bedrooms}</span>
                    </li>
                    <li className="flex flex-col items-center gap-1">
                      <span className="w-8 h-8 rounded-full bg-brand-sand/50 flex items-center justify-center">
                        <ProIcon icon="bath" family="light" className="w-4 h-4 text-brand-ocean" />
                      </span>
                      <span className="font-semibold">{property.bathrooms}</span>
                    </li>
                    <li className="flex flex-col items-center gap-1">
                      <span className="w-8 h-8 rounded-full bg-brand-sand/50 flex items-center justify-center">
                        <ProIcon icon="family-dress" family="solid" className="w-4 h-4 text-brand-ocean" />
                      </span>
                      <span className="font-semibold">{property.guests}</span>
                    </li>
                  </ul>
                  <div className="flex-1 text-right">
                    <p className="text-brand-ocean font-black text-base leading-none">
                      €{property.price.toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">/night</p>
                  </div>
                </div>
              </div>
            </Link>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
};
